import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  brandMarkRecipe,
  brandMarkSize,
  type BrandMarkShape,
  type BrandMarkSizes,
} from '../recipes/brand.recipe';
import relayMark from '../assets/brands/relay.png';
import relayWordmark from '../assets/brands/relay-wordmark.svg';
import relayArrow from '../assets/brands/relay-arrow.svg';
import squareDark from '../assets/brands/square-dark.png';
import squareLight from '../assets/brands/square-light.png';
import squareWordmarkDark from '../assets/brands/square-wordmark-dark.svg';
import squareWordmarkLight from '../assets/brands/square-wordmark-light.svg';
import roadsyncMark from '../assets/brands/roadsync.png';
import roadsyncDark from '../assets/brands/roadsync-dark.png';
import roadsyncWordmarkLight from '../assets/brands/roadsync-wordmark-light.svg';
import applePayMark from '../assets/brands/apple-pay.svg';
import googlePayMark from '../assets/brands/google-pay.svg';
import quickbooksMark from '../assets/brands/quickbooks.svg';
import urgentlyDark from '../assets/brands/urgently-dark.png';
import urgentlyLight from '../assets/brands/urgently-light.png';

export type BrandName =
  | 'relay'
  | 'square'
  | 'roadsync'
  | 'apple-pay'
  | 'google-pay'
  | 'quickbooks'
  | 'urgently';

/**
 * What the artwork is drawn in. `brand` is the mark in its own colours;
 * `dark` and `light` are the one-colour versions for sitting on a light or a
 * dark ground. Not every brand ships every ink — see `BRANDS`.
 */
export type BrandInk = 'brand' | 'dark' | 'light';
/** The symbol on its own, or the symbol with the name beside it. */
export type BrandForm = 'mark' | 'wordmark';

interface BrandArtwork {
  /** How the brand is read out when the site gives no `alt`. */
  label: string;
  mark: Partial<Record<BrandInk, string>>;
  wordmark?: Partial<Record<BrandInk, string>>;
  /** Wide when the mark itself is not square — the Apple Pay and Google Pay badges. */
  markShape?: BrandMarkShape;
  /**
   * The symbol alone, with no tile behind it: the site icon. The chat's
   * working line flies it.
   */
  glyph?: string;
}

/**
 * Every mark the library ships, by brand. The files live under
 * src/assets/brands and are imported here rather than by each site, so a
 * partner's rebrand is one file swap.
 */
export const BRANDS: Record<BrandName, BrandArtwork> = {
  relay: {
    label: 'Relay Tow',
    mark: { brand: relayMark },
    // The green arrow from the browser tab, a copy of the apps' favicon.svg.
    glyph: relayArrow,
    // The dashboard sidebar's, from its Figma (17:2788): the green figure
    // beside "Relay", outlined so it needs no font.
    wordmark: { brand: relayWordmark },
  },
  square: {
    label: 'Square',
    mark: { dark: squareDark, light: squareLight },
    wordmark: { dark: squareWordmarkDark, light: squareWordmarkLight },
  },
  roadsync: {
    label: 'RoadSync',
    mark: { brand: roadsyncMark, dark: roadsyncDark },
    wordmark: { light: roadsyncWordmarkLight },
  },
  'apple-pay': {
    label: 'Apple Pay',
    mark: { brand: applePayMark },
    markShape: 'wide',
  },
  'google-pay': {
    label: 'Google Pay',
    mark: { brand: googlePayMark },
    markShape: 'wide',
  },
  quickbooks: { label: 'QuickBooks', mark: { brand: quickbooksMark } },
  urgently: {
    label: 'Urgently',
    mark: { dark: urgentlyDark, light: urgentlyLight },
  },
};

export const BRAND_NAMES = Object.keys(BRANDS) as BrandName[];

/**
 * The file to draw: the ink asked for when the brand ships it, otherwise the
 * closest it has. A brand with only a coloured mark draws that whatever the
 * ink; a brand with no wordmark draws its mark.
 */
export function brandArtwork(
  name: BrandName,
  form: BrandForm,
  ink: BrandInk
): { src: string; shape: BrandMarkShape } {
  const brand = BRANDS[name];
  const set =
    form === 'wordmark' && brand.wordmark ? brand.wordmark : brand.mark;
  const order: BrandInk[] =
    ink === 'light'
      ? ['light', 'brand', 'dark']
      : ink === 'dark'
        ? ['dark', 'brand', 'light']
        : ['brand', 'dark', 'light'];
  const key = order.find((k) => set[k]) ?? 'brand';
  const shape: BrandMarkShape =
    set === brand.wordmark ? 'wide' : (brand.markShape ?? 'square');
  return { src: set[key]!, shape };
}

export interface BrandMarkProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  name: BrandName;
  /** One size everywhere, or per breakpoint: `{ base: 'sm', sm: 'md' }`. */
  size?: BrandMarkSizes;
  form?: BrandForm;
  ink?: BrandInk;
  /**
   * Read out by a screen reader. The brand's name unless told otherwise;
   * pass an empty string when the name is written beside the mark already.
   */
  alt?: string;
}

/**
 * A brand's mark — the product's or a partner's — at a size from the scale,
 * with nothing drawn behind or around it.
 *
 *   <BrandMark name="relay" size="lg" />
 *   <BrandMark name="square" size="sm" alt="" />           beside the word "Square"
 *   <BrandMark name="square" form="wordmark" ink="light" />  on Square's black tile
 *
 * The artwork is the library's: the files are imported here, not by the
 * site. `className` is for placement, and for a parent that sizes its icon
 * slot itself — NavItem hands its icons `size-icon-md`, and that wins.
 */
export const BrandMark = React.forwardRef<HTMLImageElement, BrandMarkProps>(
  function BrandMark(
    { name, size, form = 'mark', ink = 'brand', alt, className, ...props },
    ref
  ) {
    const { src, shape } = brandArtwork(name, form, ink);
    return (
      // design-tokens-ignore: owned by BrandMark
      <img
        ref={ref}
        src={src}
        alt={alt ?? BRANDS[name].label}
        draggable={false}
        className={cn(
          brandMarkRecipe({ shape }),
          brandMarkSize(size, shape),
          className
        )}
        {...props}
      />
    );
  }
);
